/**
 * Apply, call, reconcile -- and on a refusal, put back exactly what was there.
 *
 * This is the shape every mutation on the file browser takes, and it is a
 * plain module rather than three lines inside the store for one reason: the
 * store holds runes and needs a component to run in, and the rule this
 * function encodes is the one that must not be got wrong.
 *
 * The rule, from TASK-042 §6d: **a refusal never leaves the optimistic state
 * applied**, and the revert is ONE function over ONE record, so there is
 * exactly one code path to test rather than one per mutation.
 *
 * Two things it deliberately does NOT do:
 *
 *  - It does not await the reconcile. The screen is already correct by then;
 *    awaiting a refresh would put the stall back in the exact place the
 *    optimistic path removed it from.
 *  - It does not swallow the refusal. The caller needs the server's own
 *    sentence to put on screen, so the cause is rethrown wrapped, which is how
 *    "we reverted" and "here is why" stop being two separate questions at the
 *    call site.
 */

/** A refusal, after the optimistic change has already been taken back. */
export class Reverted extends Error {
  constructor(readonly cause: unknown) {
    super('reverted')
    this.name = 'Reverted'
  }
}

export async function optimistic<T>(
  /** Capture what is about to change, and return the undo for it. */
  snapshot: () => () => void,
  /** Change it. Runs before the request, on the caller's own frame. */
  apply: () => void,
  /** The request. */
  call: () => Promise<T>,
  /** Confirmed: bring the cache back in line, in the background. */
  reconcile: () => void = () => {},
): Promise<T> {
  const revert = snapshot()
  apply()
  let out: T
  try {
    out = await call()
  } catch (err) {
    revert()
    throw new Reverted(err)
  }
  // Outside the try: a throw from the reconcile is not a reason to undo a
  // change the server has accepted.
  reconcile()
  return out
}
